import { nhAppMainPages } from './nhAppMainPages';

export const SCOPE_KINDS = ['nursing_home', 'legal_entity', 'tenant'] as const;

export type ScopeKind = (typeof SCOPE_KINDS)[number];

interface RouteAccessRule {
  pathPrefix: string;
  restricted: boolean;
}

interface RouteScopeRule {
  pathPrefix: string;
  levels: ScopeKind[];
}

// Rules are matched by prefix. More specific (longer) prefixes take precedence.
// Routes NOT listed here are accessible to all authenticated users.
export const routeAccessRules: readonly RouteAccessRule[] = [
  // Admin/manager-only route groups
  { pathPrefix: nhAppMainPages.RECURRING_ITEMS_V2, restricted: true },
  { pathPrefix: nhAppMainPages.SERVICE_PROVIDER, restricted: true },
  { pathPrefix: nhAppMainPages.NURSING_HOME_ACCOUNTING, restricted: true },
  { pathPrefix: nhAppMainPages.NURSING_HOME, restricted: true },
  { pathPrefix: nhAppMainPages.EXPENSE_MANAGEMENT, restricted: true },

  // Exceptions: sub-routes accessible to all (more specific prefix wins)
  {
    pathPrefix: nhAppMainPages.NURSING_HOME_ACCOUNTING_CARD_PAYMENTS,
    restricted: false,
  },
];

// Which scopes a route works at. Routes NOT listed here are nursing home only,
// the default for anything that filters by a single home. Add an entry only to
// widen a route. Both the navbar and RouteAccessGuard read this, so an item
// cannot be visible at a scope the guard would bounce you off.
export const routeScopeRules: readonly RouteScopeRule[] = [
  {
    pathPrefix: nhAppMainPages.DASHBOARD,
    levels: ['nursing_home', 'legal_entity', 'tenant'],
  },
  // Expenses roll up to a legal entity, so there is nothing to show above it.
  {
    pathPrefix: nhAppMainPages.EXPENSE_MANAGEMENT,
    levels: ['nursing_home', 'legal_entity'],
  },
  // The card payment list is served by a legal-entity scoped endpoint, so it
  // works one level up. Tenant scope has no single legal entity to key it on.
  {
    pathPrefix: nhAppMainPages.TRANSACTIONS_UNASSOCIATED_CARD_PAYMENTS,
    levels: ['nursing_home', 'legal_entity'],
  },
];

const matchesRoutePrefix = (pathname: string, prefix: string): boolean =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

const mostSpecificRule = <T extends { pathPrefix: string }>(
  rules: readonly T[],
  pathname: string
): T | undefined =>
  rules
    .filter((rule) => matchesRoutePrefix(pathname, rule.pathPrefix))
    .sort((a, b) => b.pathPrefix.length - a.pathPrefix.length)[0];

export function isRouteSupportedAtScope(
  pathname: string,
  kind: ScopeKind
): boolean {
  const rule = mostSpecificRule(routeScopeRules, pathname);
  return (rule?.levels ?? ['nursing_home']).includes(kind);
}

export function isRouteAccessible(
  pathname: string,
  isAdminOrManager: boolean
): boolean {
  // The most specific (longest) matching prefix wins, so sub-routes override
  // parent rules, e.g. /accounting/card-payments (unrestricted) overrides
  // /accounting (restricted). No rule at all means open access.
  const rule = mostSpecificRule(routeAccessRules, pathname);
  return !rule?.restricted || isAdminOrManager;
}
